import React, { useState } from 'react';
import './LeadSalvageModal.css';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { updateSession } from '../utils/api';

// Phone digit-normalization mirrors src/components/PersonalInfoForm.js:13-30 so
// the salvaged lead's phone is stored the same way as the wizard stores it
// (bare 10-digit string).
const normalizePhone = (value) => {
  if (!value) return '';
  let digits = value.replace(/[^\d]/g, '');
  // Drop a leading US country code if present.
  if (digits.startsWith('1') && digits.length === 11) {
    digits = digits.substring(1);
  }
  return digits.slice(0, 10);
};

const formatPhoneForDisplay = (value) => {
  const digits = normalizePhone(value);
  const length = digits.length;
  if (length === 0) return '';
  if (length < 4) return digits;
  if (length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Light email check to match the rest of the widget's validation lightness.
const isValidEmail = (email) => !!email && email.includes('@');

/**
 * CIN-6274 lead-salvage modal.
 *
 * Rendered by LeadSalvageContext when a consumer abandons the widget without
 * contact info. Reuses the hand-rolled .modal-overlay/.modal-content shells and
 * the --primary-* branding vars; salvage-specific styling lives in
 * LeadSalvageModal.css.
 *
 * Submit-enable rule (AC is source of truth, resolves the Figma email `*`):
 * enabled when a valid email OR a phone is present. Name does not gate
 * submission (QA on CIN-6274: "at least a valid email or phone number" must
 * function as OR, with nothing else required). Email is NOT mandatory.
 *
 * On submit, reuses the existing updateSession() PATCH to attach contact info to
 * the already-created session. On success -> onSuccess() (raw close). On
 * failure -> inline error, modal stays mounted for retry.
 */
const LeadSalvageModal = ({ calendarId, sessionId, onSuccess, onDismiss }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [displayPhone, setDisplayPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const phoneDigits = normalizePhone(displayPhone);
  const emailValid = isValidEmail(email.trim());
  const phoneValid = phoneDigits.length === 10 || phoneDigits.length === 11; // allow leading 1 for US country code
  const canSubmit = (emailValid || phoneValid) && !submitting;

  const handlePhoneChange = (e) => {
    setDisplayPhone(formatPhoneForDisplay(e.target.value));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    // Split the single Name field into first/last (first token = first name,
    // the remainder = last name).
    const trimmed = name.trim();
    const spaceIdx = trimmed.indexOf(' ');
    const firstName = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx);
    const lastName = spaceIdx === -1 ? '' : trimmed.slice(spaceIdx + 1).trim();

    try {
      // Partial PATCH: send only contact fields. DRF PATCH preserves the
      // session's existing partial booking data (services/location/current_step/
      // entity_data) so the salvaged lead keeps whatever was already selected.
      // salvage_submit:true opts THIS request into backend lead dedup (CIN-6274
      // backend layer); the normal progressive session saves in SessionContext
      // must NOT send it, so dedup never soft-deletes a live in-progress session.
      // Backend dedup may fold this into an existing lead and return a different
      // session id; we ignore the body and close.
      await updateSession(calendarId, sessionId, {
        first_name: firstName || null,
        last_name: lastName || null,
        email: email.trim() || null,
        phone: phoneDigits || null,
        salvage_submit: true,
      });
      onSuccess();
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay salvage-modal-overlay">
      <div className="modal-content salvage-modal-content">
        <button
          type="button"
          className="salvage-close"
          aria-label="Close"
          onClick={onDismiss}
        >
          <ClearOutlinedIcon fontSize="small" />
        </button>

        <h3 className="salvage-title">We'd Love to Follow Up</h3>
        <p className="salvage-subtitle">
          Leave your contact information and someone will reach out to help you
          finish booking.
        </p>

        <div className="salvage-fields">
          <input
            className="salvage-input"
            type="text"
            placeholder="Name"
            aria-label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="salvage-input-wrap">
            <input
              className="salvage-input"
              type="email"
              placeholder="Email"
              aria-label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {/* Figma `*` = "at least one contact method required", not
                "email mandatory" (see submit-enable rule above). */}
            <span className="salvage-required" aria-hidden="true">
              *
            </span>
          </div>
          <input
            className="salvage-input"
            type="tel"
            placeholder="Phone"
            aria-label="Phone"
            value={displayPhone}
            onChange={handlePhoneChange}
          />
        </div>

        {error && (
          <div className="salvage-error" role="alert">
            {error}
          </div>
        )}

        <button
          type="button"
          className="salvage-submit"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? 'Submitting...' : 'Contact Me'}
        </button>

        <button type="button" className="salvage-dismiss" onClick={onDismiss}>
          No Thanks, I'll Book Another Time
        </button>
      </div>
    </div>
  );
};

export default LeadSalvageModal;
